import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { resolveLocale, type Locale } from '@tb/shared';
import { useSession } from '../auth/SessionProvider';
import { webApp } from '../telegram/telegram';
import { DICTIONARY, type MessageKey } from './dictionary';

export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

interface LocaleValue {
  locale: Locale;
  t: Translate;
}

const LocaleContext = createContext<LocaleValue | null>(null);

export function detectLocale(languageCode?: string | null, chosen?: Locale | null): Locale {
  if (chosen) return chosen;
  if (languageCode) return resolveLocale(languageCode);

  const fromTelegram = webApp()?.initDataUnsafe?.user?.language_code;
  if (fromTelegram) return resolveLocale(fromTelegram);

  return resolveLocale(typeof navigator === 'undefined' ? null : navigator.language);
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const languageCode = session.status === 'ready' ? session.user.languageCode : null;
  const chosen = session.status === 'ready' ? session.user.locale : null;

  const value = useMemo<LocaleValue>(() => {
    const locale = detectLocale(languageCode, chosen);

    const t: Translate = (key, vars) => {
      let result = DICTIONARY[key][locale];
      if (vars) {
        for (const [name, replacement] of Object.entries(vars)) {
          result = result.split(`{${name}}`).join(String(replacement));
        }
      }
      return result;
    };

    return { locale, t };
  }, [languageCode, chosen]);

  document.documentElement.lang = value.locale;

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale used outside LocaleProvider');
  return value;
}

export function useT(): Translate {
  return useLocale().t;
}
